// #806 Back Office bildirim çanı.
//
// ## Neden bu dosya var
// Bildirim TANIM ekranı (Bildirimler > Gönder) kritere uyan HER kullanıcı için bir
// `Notification` satırı yazıyordu — Merkez/Distribütör dahil. Ama web panelinde bu
// satırları OKUYAN hiçbir şey yoktu: üst bardaki çan `AnnouncementBell`'dir ve
// `/api/announcements/Active` (DUYURU) ucuna bakar, `/api/notifications` ucuna değil.
// Yani bildirim üretiliyor, DB'ye yazılıyor, push mobil cihazlara gidiyor; Back Office
// kullanıcısı ise kendisine gönderilen bildirimi HİÇBİR YERDE göremiyordu.
// Teslim hattındaki kopuk halka buydu — bu bileşen onu kapatır.
//
// ## Neden polling, neden SignalR değil
// Projede gerçek-zamanlı bir altyapı YOK: `@microsoft/signalr` bağımlılığı yok, backend'de
// tek bir Hub yok. Mobil taraf FCM ile anlık alır ama web SPA hiç FCM token'ı kaydetmez
// (`FcmToken` yalnız mobil cihazlardan gelir). Yeni bir gerçek-zamanlı altyapı kurmak bu
// işin kapsamı değildir; panelin zaten kullandığı yol polling'dir (AnnouncementBell 5 dk,
// SyncStatusBadge, VisitTrackingPage). Burada aynı yol kullanılır, ama "anlık" hissi için:
//   • aralık 45 sn (duyuru çanının 5 dakikası değil),
//   • sekme yeniden görünür olunca ANINDA tazelenir (arka planda interval kısılır),
//   • panel açılınca tazelenir.
// Uç maliyeti düşüktür: `GET /api/notifications` tek kullanıcıya kapalı, sayfalı bir sorgu
// + tek bir COUNT'tur.
//
// ## Neden duyurular da burada
// Üst barda iki özdeş çan vardı: bu (bildirim) ve `AnnouncementBell` (duyuru). Kullanıcı için
// ikisi ayırt edilemiyordu. Tek çanda birleştirildi; panel iki bölümdür — önce DUYURULAR
// (süreli ilan, okundu bilgisi yoktur), sonra BİLDİRİMLER (kişiye özel, okundu/okunmadı).
// Rozet YALNIZ okunmamış bildirimi sayar: duyurunun okundu durumu olmadığı için sayılsaydı
// aktif duyuru olduğu sürece rozet sönmez, "okunmamış" anlamını yitirirdi.
import { useCallback, useEffect, useRef, useState } from 'react';
import { authHeaders } from '../../utils/odata';
import { useActiveAnnouncements, AnnouncementViewer } from '../AnnouncementBell';
import './NotificationBell.css';

const POLL_MS = 45 * 1000;
const TAKE = 20;

const unwrap = (p) => (p && (p.Data ?? p.data)) ?? p ?? {};

const TYPE_LABEL = {
  SYSTEM: 'Sistem',
  CAMPAIGN: 'Kampanya',
  ORDER_STATUS: 'Sipariş',
  TASK_ASSIGNED: 'Görev',
  POINT_OPENING: 'Nokta Açma',
  TRAINING: 'Eğitim',
  STOCK_TRANSFER: 'Stok Transferi',
};

function timeAgo(value) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} sa önce`;
  const day = Math.floor(hour / 24);
  if (day < 7) return `${day} gün önce`;
  return new Date(value).toLocaleDateString('tr-TR');
}

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { announcements, refreshAnnouncements } = useActiveAnnouncements();
  const [viewerIndex, setViewerIndex] = useState(null); // null = okuyucu kapalı
  const wrapRef = useRef(null);
  // StrictMode/yeniden kurulumda geç dönen isteğin unmount sonrası state yazmasını engeller.
  const aliveRef = useRef(true);

  const fetchList = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setItems([]);
      setUnread(0);
      return;
    }
    try {
      const res = await fetch(`/api/notifications?take=${TAKE}`, { headers: authHeaders() });
      if (!res.ok) return; // sessiz: çan bir hata yüzeyi değil, 401 akışı authInterceptor'ın işi
      const data = unwrap(await res.json());
      if (!aliveRef.current) return;
      setItems(Array.isArray(data.items) ? data.items : []);
      setUnread(Number(data.unreadCount) || 0);
    } catch {
      /* ağ hatası: bir sonraki tur dener, mevcut liste korunur */
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    fetchList();
    const timer = window.setInterval(fetchList, POLL_MS);
    // Sekme arka plandayken tarayıcı interval'i kısar; öne gelince gecikmeyi kapatmak için
    // hemen tazele. "Anlık" hissini asıl veren budur.
    const onVisible = () => { if (document.visibilityState === 'visible') fetchList(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      aliveRef.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchList]);

  // Panel dışına tıklayınca kapan.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Yan etki state güncelleyicisinin İÇİNDE çağrılmaz: StrictMode updater'ı iki kez çalıştırır
  // ve liste iki kez çekilirdi.
  const togglePanel = () => {
    if (!open) { fetchList(); refreshAnnouncements(); } // açılışta taze liste
    setOpen((o) => !o);
  };

  const markRead = async (id) => {
    // İyimser güncelleme: tık anında okundu görünsün, sunucu arkadan onaylasın.
    setItems((prev) => prev.map((n) => ((n.id ?? n.Id) === id ? { ...n, isRead: true, IsRead: true } : n)));
    setUnread((c) => Math.max(0, c - 1));
    try {
      await fetch(`/api/notifications/${id}/mark-read`, { method: 'POST', headers: authHeaders() });
    } catch { /* yoksay — sonraki polling gerçek durumu geri getirir */ }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await fetch('/api/notifications/mark-all-read', { method: 'POST', headers: authHeaders() });
      await fetchList();
    } catch { /* yoksay */ } finally { setBusy(false); }
  };

  const badge = unread > 99 ? '99+' : String(unread);
  // Rozet okunmamış BİLDİRİMİ sayar; duyuru sayısı yalnız ipucu metnine girer.
  const label = [
    unread > 0 ? `${unread} okunmamış bildirim` : null,
    announcements.length > 0 ? `${announcements.length} aktif duyuru` : null,
  ].filter(Boolean).join(' · ') || 'Bildirimler';

  return (
    <div className="ntf-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ntf-bell${unread > 0 ? ' has-unread' : ''}`}
        onClick={togglePanel}
        aria-expanded={open}
        aria-label={label}
        title={label}
      >
        <BellIcon />
        {unread > 0 && <span className="ntf-bell-badge">{badge}</span>}
      </button>

      {open && (
        <div className="ntf-panel" role="dialog" aria-label="Bildirimler ve duyurular">
          <div className="ntf-panel-head">
            <strong>Bildirimler</strong>
            {unread > 0 && (
              <button type="button" className="ntf-panel-mark" onClick={markAllRead} disabled={busy}>
                Tümünü okundu işaretle
              </button>
            )}
          </div>

          <div className="ntf-panel-list">
            {/* Duyurular önce: kişiye özel değil, herkese açık ve süreli ilanlardır. */}
            {announcements.length > 0 && (
              <div className="ntf-section-head">Duyurular · {announcements.length}</div>
            )}
            {announcements.map((a, i) => (
              <button
                key={a.id ?? a.Id ?? i}
                type="button"
                className="ntf-item ntf-item-announcement"
                onClick={() => { setViewerIndex(i); setOpen(false); }}
              >
                <span className="ntf-item-dot" aria-hidden="true" />
                <span className="ntf-item-main">
                  <span className="ntf-item-title">{a.title}</span>
                  <span className="ntf-item-body">{a.description || 'Açıklama girilmemiş.'}</span>
                  <span className="ntf-item-meta">Duyuru</span>
                </span>
              </button>
            ))}

            {(announcements.length > 0 && items.length > 0) && (
              <div className="ntf-section-head">Bildirimler</div>
            )}
            {items.length === 0 && announcements.length === 0 && (
              <div className="ntf-panel-empty">Bildiriminiz yok.</div>
            )}
            {items.map((n) => {
              const id = n.id ?? n.Id;
              const isRead = n.isRead ?? n.IsRead;
              const type = n.type ?? n.Type;
              return (
                <button
                  key={id}
                  type="button"
                  className={`ntf-item${isRead ? '' : ' unread'}`}
                  onClick={() => !isRead && markRead(id)}
                >
                  <span className="ntf-item-dot" aria-hidden="true" />
                  <span className="ntf-item-main">
                    <span className="ntf-item-title">{n.title ?? n.Title}</span>
                    <span className="ntf-item-body">{n.body ?? n.Body}</span>
                    <span className="ntf-item-meta">
                      {TYPE_LABEL[type] || type || 'Sistem'} · {timeAgo(n.createdDate ?? n.CreatedDate)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {viewerIndex != null && (
        <AnnouncementViewer
          items={announcements}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

export default NotificationBell;
